/**
 * `/$org/workflows/$workflowId`: one workflow's chart.
 *
 * The chart is AmoregGTM's canvas (`./canvas`, AMO-177): steps flow top to
 * bottom. Editing (no run selected): drag steps out of the Steps panel or
 * press one to add it after the selected step, drag cards, draw links from
 * an outlet to a step, Tidy to lay the chart out, undo and redo, and edit a
 * step in its panel (Edit, double-click or Enter). A chart saved before
 * AMO-177 (left to right) is laid out top to bottom when it first opens.
 * Changes save themselves a moment after they stop, and a colleague's or
 * eve's newer version merges into what is on screen, the person's unsaved
 * edits winning wherever both changed one thing (`state/workflowDoc.ts`).
 * Undo takes back the person's own last edit and keeps anyone else's since,
 * or a whole request eve made for them when that is newer, kept on the server
 * so a reload keeps it. ⌘Z in a text field stays the field's own undo.
 * Problems that stop the workflow running
 * (`validateWorkflowGraph`, the server's own rules) mark their steps and
 * keep Run disabled.
 *
 * A run (`?run=<id>`): the frozen chart that run walked, each step marked
 * by its state and updating live, the links it travelled lit; a failed step
 * carries its reason in a bubble beside it with Retry. Stop ends a run in
 * flight. The panel shows a
 * step's result or its error in full, and a Browser task step's chat.
 *
 * A Browser task step chooses one of the viewer's browser tasks, which open
 * in their own dialog over the chart.
 *
 * @module components/workflows/WorkflowPage
 */
import type {
  Workflow,
  WorkflowGraph,
  WorkflowGraphIssue,
  WorkflowLockEvent,
  WorkflowNode,
  WorkflowNodeType,
  WorkflowRunFile,
  WorkflowRunSummary,
  WorkflowScheduleView,
  WorkflowSharing,
} from "@amoreg/contracts";
import {
  addLink,
  duplicateStep,
  graphForSave,
  insertStepOnLink,
  linkRefusal,
  type LinkRefusal,
  moveSteps,
  newStep,
  removeSteps,
  updateStep,
} from "@repo/shared/workflowEdit";
import { validateWorkflowGraph } from "@repo/shared/workflowGraph";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CalendarClockIcon,
  ChevronDownIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  EllipsisIcon,
  HistoryIcon,
  LockIcon,
  LockOpenIcon,
  PlayIcon,
  Share2Icon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useAccountProfile } from "../../state/accountProfile";
import { useBrowserTasks } from "../../state/browserTasks";
import { useOrgProfile } from "../../state/orgProfile";
import { useActiveOrg } from "../../state/orgs";
import {
  cancelWorkflowRun,
  deleteWorkflow,
  isActiveRun,
  retryWorkflowStep,
  setWorkflowSchedule,
  startWorkflowRun,
  useWorkflow,
  useWorkflowDoc,
  useWorkflowRun,
} from "../../state/workflows";
import { noticeText, type SaveStatus, type WorkflowDocNotice } from "../../state/workflowDoc";
import { useWorkflowPresence } from "../../state/workflowPresence";
import { formatRelativeTimeLabel, formatRelativeTimeUntilLabel } from "../../timestampFormat";
import { browserTimezone, SchedulePicker } from "../automations/SchedulePicker";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Button } from "../ui/button";
import { Kbd } from "../ui/kbd";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../ui/dialog";
import { AmoregMark } from "../sidebar/SidebarChrome";
import { SidebarTrigger } from "../ui/sidebar";
import { Menu, MenuGroup, MenuGroupLabel, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../ui/menu";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import {
  canRetryRun,
  issuesByNode,
  scheduleDraftOf,
  scheduleRuleOf,
  stepsByNode,
  stepSubtitle,
} from "./workflowCanvas.logic";
import { BrowserTaskDialog } from "./BrowserTasks";
import { WorkflowShareDialog } from "./WorkflowShareDialog";
import { WorkflowLockDialog } from "./WorkflowLockDialog";
import { lockDiscardedEdits, lockSummary, lockWaitsForSave } from "./WorkflowLockDialog.logic";
import { browserTaskNames } from "./BrowserTasks.logic";
import { needsRelayout, tidyWorkflowGraph } from "./workflowLayout";
import { ApprovalDecision } from "./ApprovalDecision";
import { RunBar } from "./RunBar";
import { RUN_FILE_ACCEPT, runFilePayload, runFileProblem } from "./RunFiles.logic";
import { ChecklistDone } from "./ChecklistDone";
import { AskEvePanel, AskEveTab, claimKeyForPanel, focusContainerOnPress, useAskEvePanel } from "./AskEvePanel";
import { askEveTabShown, panelChartState } from "./AskEvePanel.logic";
import { ChangeBar } from "./ChangeBar";
import { eveAtWork, useDismissedRequests, useWorkflowChanges } from "./workflowChanges";
import { MailboxReads, MailboxWait } from "./MailboxAccess";
import { StepFieldScope } from "./StepFieldScope";
import { RunStatusPill, StepEditorPanel, StepQuickEdit, StepQuickRun, StepRunPanel, type ApproverChoice } from "./workflowParts";
import { freePlacement, handleOfPort, NODE_WIDTH, type Point, type PortId } from "./canvas/geometry";
import { KINDS, kindClass, PALETTE } from "./canvas/kinds";
import { GLASS_CARD, GLASS_PILL } from "./canvas/panels";
import { PeopleHere } from "./canvas/presence";
import { peopleOf } from "./canvas/presenceTracks";
import { RowInsertIcon } from "./canvas/icons";
import { type CanvasRemoval, type ChartKeys, WorkflowCanvas } from "./canvas/WorkflowCanvas";

/** The page fills the card beside Home's sidebar, as the Space page does. */
const SURFACE = "flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-background text-foreground";

function useTickingNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

function RunDialog({
  open,
  onOpenChange,
  hint,
  filesHint,
  onStart,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly hint: string | undefined;
  /** What files the Start step asks for (AMO-304); undefined when it asks for none. */
  readonly filesHint: string | undefined;
  readonly onStart: (
    input: string,
    files: ReadonlyArray<WorkflowRunFile>,
  ) => Promise<{ readonly message: string; readonly issues?: ReadonlyArray<WorkflowGraphIssue> } | null>;
}) {
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<ReadonlyArray<File>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ readonly message: string; readonly issues?: ReadonlyArray<WorkflowGraphIssue> } | null>(null);
  const addFiles = (picked: ReadonlyArray<File>) => {
    let next = [...files];
    for (const file of picked) {
      const problem = runFileProblem(file, next.length);
      if (problem) {
        setError({ message: problem });
        continue;
      }
      next = [...next, file];
    }
    setFiles(next);
  };
  const start = async () => {
    setBusy(true);
    setError(null);
    const payload = await Promise.all(files.map(runFilePayload)).catch(() => null);
    if (payload === null) {
      setBusy(false);
      setError({ message: "A file could not be read in the browser. Pick it again." });
      return;
    }
    const refused = await onStart(input, payload);
    setBusy(false);
    if (refused) {
      setError(refused);
      return;
    }
    setInput("");
    setFiles([]);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Run workflow</DialogTitle>
          <DialogDescription>
            Each step runs as you, on the same models as chat. You can leave the page: the run carries on.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-2">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">{hint?.trim() || "Input for this run (optional)"}</span>
            <Textarea
              autoFocus
              rows={6}
              value={input}
              maxLength={50_000}
              onChange={(event) => setInput(event.currentTarget.value)}
              placeholder="What the first steps should work on"
              data-workflow-run-input=""
            />
          </label>
          {filesHint !== undefined ? (
            <div className="space-y-1.5" data-workflow-run-files="">
              <span className="text-xs font-medium">{filesHint.trim() || "Files for this run"}</span>
              {files.length > 0 ? (
                <ul className="space-y-1">
                  {files.map((file, index) => (
                    // oxlint-disable-next-line react/no-array-index-key -- the same file may be picked twice
                    <li key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1 text-[12.5px]">
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      <Button size="icon-xs" variant="ghost" aria-label={`Remove ${file.name}`} onClick={() => setFiles(files.filter((_, at) => at !== index))}>
                        <XIcon />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <input
                type="file"
                multiple
                accept={RUN_FILE_ACCEPT}
                className="block w-full text-xs file:me-2 file:rounded-md file:border-0 file:bg-muted file:px-2 file:py-1"
                onChange={(event) => {
                  addFiles([...(event.currentTarget.files ?? [])]);
                  event.currentTarget.value = "";
                }}
                data-workflow-run-file-input=""
              />
              <span className="block text-[11px] leading-snug text-muted-foreground">
                Up to 3 PDF, Word, Excel, CSV or text files of 4 MB each. The first steps read them after what you typed;
                spreadsheets come through as tables.
              </span>
            </div>
          ) : null}
          {error ? (
            <div className="space-y-1 text-sm text-destructive">
              <p>{error.message}</p>
              {error.issues?.map((issue, index) => (
                <p key={`${issue.code}-${issue.nodeId ?? issue.edgeId ?? index}`} className="text-xs">
                  · {issue.message}
                </p>
              ))}
            </div>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void start()} disabled={busy} data-workflow-run-start="">
            <PlayIcon />
            {busy ? "Starting…" : "Start run"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

/**
 * When the workflow runs by itself. Each scheduled run starts with the saved
 * input and acts as the person who last saved the schedule: their access,
 * their allowance.
 */
function ScheduleDialog({
  open,
  onOpenChange,
  schedule,
  organizationId,
  workflowId,
  runBlockedReason,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly schedule: WorkflowScheduleView | null;
  readonly organizationId: string;
  readonly workflowId: string;
  readonly runBlockedReason: string | null;
}) {
  const [draft, setDraft] = useState(() => scheduleDraftOf(schedule, browserTimezone()));
  const [input, setInput] = useState(schedule?.input ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Each opening starts from what is saved now.
  useEffect(() => {
    if (!open) return;
    setDraft(scheduleDraftOf(schedule, browserTimezone()));
    setInput(schedule?.input ?? "");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on opening
  }, [open]);
  const submit = async (turnOff: boolean) => {
    setBusy(true);
    setError(null);
    const outcome = await setWorkflowSchedule(
      organizationId,
      workflowId,
      turnOff ? { rule: null } : { rule: scheduleRuleOf(draft), input },
    );
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule</DialogTitle>
          <DialogDescription>
            The workflow runs by itself at these times. Each run acts as you, the person saving the schedule, on your
            access, and counts toward your allowance.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3" data-workflow-schedule-dialog="">
          {schedule ? (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-[13px]" data-workflow-schedule-current="">
              <p className="font-medium">{schedule.description}</p>
              <p className="text-xs text-muted-foreground">
                {schedule.nextRunAt ? `Next run ${formatRelativeTimeUntilLabel(schedule.nextRunAt)}` : "No run left"}
                {schedule.ownerEmail ? ` · as ${schedule.ownerEmail}` : ""}
              </p>
              {schedule.lastError ? <p className="mt-1 text-xs text-destructive">{schedule.lastError}</p> : null}
            </div>
          ) : null}
          <SchedulePicker schedule={draft} onChange={setDraft} />
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Input for each scheduled run (optional)</span>
            <Textarea
              rows={4}
              value={input}
              maxLength={50_000}
              onChange={(event) => setInput(event.currentTarget.value)}
              placeholder="What the first steps should work on"
              data-workflow-schedule-input=""
            />
          </label>
          {runBlockedReason ? (
            <p className="text-xs text-warning-foreground">
              {runBlockedReason}: a scheduled run that cannot start is skipped, and the reason shows here.
            </p>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </DialogPanel>
        <DialogFooter>
          {schedule ? (
            <Button
              size="sm"
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={() => void submit(true)}
              disabled={busy}
              data-workflow-schedule-off=""
            >
              Turn off
            </Button>
          ) : null}
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void submit(false)} disabled={busy} data-workflow-schedule-save="">
            <CalendarClockIcon />
            {busy ? "Saving…" : schedule ? "Save schedule" : "Turn on"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function ConfirmDeleteDialog({
  open,
  onOpenChange,
  name,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly name: string;
  readonly onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete “{name}”?</DialogTitle>
          <DialogDescription>The workflow leaves the list. Runs already made are kept.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onConfirm().finally(() => setBusy(false));
            }}
          >
            {busy ? "Deleting…" : "Delete workflow"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

const SAVE_LABEL: Readonly<Record<SaveStatus, string>> = {
  saved: "Saved",
  unsaved: "Unsaved changes",
  saving: "Saving…",
  error: "Not saved",
};

const NOTICE_TYPE: Partial<Record<WorkflowDocNotice["kind"], "info" | "error">> = {
  "kept-yours": "info",
  "left-unsaved": "error",
  "request-failed": "error",
};

/** One toast per kind of notice: a later one replaces it rather than stacking. */
const showNotice = (notice: WorkflowDocNotice) =>
  toastManager.add({
    id: `workflow-notice-${notice.kind}`,
    type: NOTICE_TYPE[notice.kind] ?? "warning",
    title: noticeText(notice),
  });

/** Why a link drawn on the chart was not made. */
const LINK_REFUSAL: Readonly<Record<Exclude<LinkRefusal, "duplicate">, string>> = {
  self: "A step cannot link to itself.",
  "into-start": "Nothing links into Start.",
  "from-output": "Output ends a workflow: nothing links out of it.",
  loop: "That link would make a loop.",
  missing: "That step is gone.",
  outlet: "A Condition leaves by Yes or No.",
};

/**
 * GTM's check pill, top right: while anything stops the workflow running, a
 * count that opens the list; once nothing does, a check alone.
 */
function IssuesPill({
  issues,
  graph,
  onPick,
}: {
  readonly issues: ReadonlyArray<WorkflowGraphIssue>;
  readonly graph: WorkflowGraph;
  readonly onPick: (nodeId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (issues.length === 0) {
    return (
      <span
        role="img"
        aria-label="Ready to run"
        title="Ready to run"
        className={cn(GLASS_PILL, "pointer-events-auto size-9 justify-center p-0")}
        data-workflow-ready=""
      >
        <CircleCheckIcon className="size-4 text-(--tone-green)" />
      </span>
    );
  }
  const summary = issues.length === 1 ? "1 problem to fix before it runs" : `${issues.length} problems to fix before it runs`;
  return (
    <div className="pointer-events-auto relative">
      <button
        type="button"
        aria-label={summary}
        title={summary}
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
        className={cn(GLASS_PILL, "cursor-pointer gap-1.5 pr-3 pl-2.5 transition-colors duration-150 hover:bg-foreground/5")}
        data-workflow-issues=""
      >
        <CircleAlertIcon className="size-4 text-(--tone-red)" />
        <span className="font-medium text-foreground tabular-nums">{issues.length}</span>
      </button>
      {open ? (
        <ul className={cn(GLASS_CARD, "absolute top-full right-0 mt-2 w-[min(440px,calc(100vw-32px))] animate-canvas-rise-in overflow-hidden p-1.5")}>
          {issues.map((issue, index) => (
            <li key={`${issue.code}-${issue.nodeId ?? issue.edgeId ?? index}`}>
              <button
                type="button"
                onClick={() => {
                  if (issue.nodeId) onPick(issue.nodeId);
                  setOpen(false);
                }}
                className="flex w-full cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] leading-5 text-foreground transition-colors duration-100 hover:bg-foreground/5"
              >
                <CircleAlertIcon className="mt-0.5 size-4 shrink-0 text-(--tone-red)" />
                <span>
                  {issue.nodeId ? <b>{graph.nodes.find((node) => node.id === issue.nodeId)?.label}: </b> : null}
                  {issue.message}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** GTM's panel footer: walk on to the next step, or add one after this step. */
function NextStepFooter({
  node,
  graph,
  onWalk,
  onAdd,
}: {
  readonly node: WorkflowNode;
  readonly graph: WorkflowGraph;
  readonly onWalk: (nodeId: string) => void;
  readonly onAdd: (kind: WorkflowNodeType) => void;
}) {
  if (node.type === "output") return null;
  const next = graph.edges.find((edge) => edge.source === node.id);
  if (next) {
    return (
      <Button className="w-full" size="lg" onClick={() => onWalk(next.target)} data-workflow-next-step="">
        <RowInsertIcon className="size-[18px] -scale-y-100" />
        <span className="font-semibold">Next step</span>
      </Button>
    );
  }
  return (
    <Menu>
      <MenuTrigger render={<Button className="w-full" size="lg" data-workflow-add-next="" />}>
        <RowInsertIcon className="size-[18px] -scale-y-100" />
        <span className="font-semibold">Add next step</span>
      </MenuTrigger>
      <MenuPopup side="top" align="center" className="w-(--anchor-width)">
        {PALETTE.map((group) => (
          <MenuGroup key={group.label}>
            <MenuGroupLabel className="uppercase">{group.label}</MenuGroupLabel>
            {group.kinds.map((kind) => {
              const meta = KINDS[kind];
              return (
                <MenuItem key={kind} className={kindClass(kind)} onClick={() => onAdd(kind)} data-workflow-add-next-kind={kind}>
                  <meta.icon className="size-4 text-kind" />
                  {meta.label}
                </MenuItem>
              );
            })}
          </MenuGroup>
        ))}
      </MenuPopup>
    </Menu>
  );
}

function WorkflowChart({
  organizationId,
  workflow,
  runs,
  runId,
  stepId,
  chatId,
  onSelectRun,
  sharing,
  lockHistory,
}: {
  readonly organizationId: string;
  readonly workflow: Workflow;
  /** A chat to open Ask eve on (a chat's link to this chart). */
  readonly chatId: string | null;
  /** Its locks and unlocks, newest first: for those who may edit it. */
  readonly lockHistory: ReadonlyArray<WorkflowLockEvent>;
  /** Who it is shared with: present for its owner only. */
  readonly sharing: WorkflowSharing | null;
  readonly runs: ReadonlyArray<WorkflowRunSummary>;
  readonly runId: string | null;
  /** A step to open in the panel at first (an Approval step, from the Inbox). */
  readonly stepId: string | null;
  readonly onSelectRun: (runId: string | null) => void;
}) {
  const navigate = useNavigate();
  const org = orgUrlSegment(organizationId);
  const chartKeys = useRef<ChartKeys | null>(null);
  // What the reader may do: an older server sends no role, and then everyone could edit.
  const role = workflow.role ?? "edit";
  // What the chart is to Ask eve as the page opens: a first visit to a blank one opens the panel.
  const canEditAtOpen = role !== "run" && (workflow.lock ?? null) === null;
  const [chartAtOpen] = useState(() =>
    panelChartState({ viewingRun: runId !== null, editable: canEditAtOpen, graph: workflow.graph }),
  );
  // The refs apart, so reading the rest during render keeps the chart compiled.
  const { buttonRef: askEveButtonRef, panelRef: askEvePanelRef, ...askEve } = useAskEvePanel(chartKeys, {
    workflowId: workflow.id,
    state: chartAtOpen,
  });
  const { openOn: openAskEveOn } = askEve;
  useEffect(() => {
    if (chatId) openAskEveOn(chatId, "composer");
  }, [chatId, openAskEveOn]);
  const chartRef = useRef<HTMLDivElement>(null);
  // A step, or a step's panel, that goes while holding focus would drop it to
  // the body, where Backspace is nobody's while Ask eve is open; the chart
  // column takes it first, so Backspace still deletes a step left selected.
  const holdChartFocus = useCallback(() => {
    if (chartRef.current?.contains(document.activeElement)) chartRef.current.focus({ preventScroll: true });
  }, []);

  // The step whose panel is open.
  const [selectedId, setSelectedId] = useState<string | null>(stepId);
  const openStep = useCallback((id: string | null) => {
    // Only focus in the closing panel goes with it; a focused step stays.
    if (id === null && document.activeElement?.closest("[data-workflow-panel]")) chartRef.current?.focus({ preventScroll: true });
    setSelectedId(id);
  }, []);
  // Bumped when the chart should fit itself again (a tidy, a relayout).
  const [fitTick, setFitTick] = useState(0);
  const [runDialog, setRunDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  // May edit it at all; and can now, unless it is locked (AMO-305). Locked, it still runs on its schedule.
  const mayEdit = role !== "run";
  const lock = workflow.lock ?? null;
  const canEdit = mayEdit && lock === null;
  const [lockDialog, setLockDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  // The browser task open in its dialog: an id, or null for a new one.
  const [taskDialog, setTaskDialog] = useState<{ readonly taskId: string | null } | null>(null);
  // What the server refused the last Run for, by step (a draft or vanished browser task
  // the chart cannot know of), with the graph it was about: the next edit drops it.
  const [runRefusals, setRunRefusals] = useState<{
    readonly graph: WorkflowGraph;
    readonly issues: ReadonlyArray<WorkflowGraphIssue>;
  } | null>(null);

  const browserTasksView = useBrowserTasks(organizationId);
  const browserTasks = browserTasksView.read?.kind === "ready" ? browserTasksView.read.value : null;
  const taskNames = useMemo(() => (browserTasks ? browserTaskNames(browserTasks) : undefined), [browserTasks]);

  const runView = useWorkflowRun(organizationId, runId);
  // Who else has this chart open (the editing chart, or this run's), and this person's share of it.
  const presenceRef = useMemo(() => ({ organizationId, workflowId: workflow.id, runId }), [organizationId, workflow.id, runId]);
  const presence = useWorkflowPresence(presenceRef);
  const people = useMemo(() => peopleOf(presence.peers), [presence.peers]);
  const run = runId && runView.read?.kind === "ready" ? runView.read.value : null;
  const viewing = runId !== null;
  // Shared to run only: the chart is read, never changed.
  const locked = viewing || !canEdit;
  const now = useTickingNow(isActiveRun(run));

  // The chart as the person sees it: what the server confirmed, their unsaved
  // edits, and every newer version merged in as it arrives.
  const dismissedRequests = useDismissedRequests();
  const { hide: hideRequest } = dismissedRequests;
  const onDocNotice = useCallback(
    (notice: WorkflowDocNotice) => {
      if (notice.kind === "request-already-undone") hideRequest(notice.group);
      showNotice(notice);
    },
    [hideRequest],
  );
  const doc = useWorkflowDoc({ organizationId, workflow, canEdit, presence, onNotice: onDocNotice });
  const changes = useWorkflowChanges(presence.onWorkflowEvent, doc.subscribe, dismissedRequests);
  const { graph, name, status: saveState, saveError, edit, adjust, undo, undoRequest, redo, currentGraph } = doc;
  const [showSteps, setShowSteps] = useState<{ readonly key: number; readonly steps: ReadonlyArray<string> } | null>(null);

  // A chart saved before AMO-177 was laid out left to right: lay it out top
  // to bottom once. An editor's copy then saves itself; a reader's is only shown.
  // If the layout fails the chart is shown as saved rather than left covered.
  const [relayoutFailed, setRelayoutFailed] = useState(false);
  useEffect(() => {
    if (!needsRelayout(graph)) return;
    let live = true;
    tidyWorkflowGraph(graph).then(
      (laid) => {
        if (!live) return;
        const positions = new Map(laid.nodes.map((node) => [node.id, node.position]));
        adjust((current) => (needsRelayout(current) ? { ...moveSteps(current, positions), layout: "down" } : current));
        setFitTick((tick) => tick + 1);
      },
      () => {
        if (live) setRelayoutFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [graph, adjust]);
  // A run's frozen chart from before AMO-177 is laid out for showing, once per run.
  const [runLaid, setRunLaid] = useState<{ readonly runId: string; readonly graph: WorkflowGraph } | null>(null);
  const runNeedsLayout = run ? needsRelayout(run.graph) : false;
  useEffect(() => {
    if (!run || !runNeedsLayout || runLaid?.runId === run.id) return;
    let live = true;
    const runIdLaid = run.id;
    void tidyWorkflowGraph(run.graph).then((laid) => {
      if (!live) return;
      setRunLaid({ runId: runIdLaid, graph: laid });
      setFitTick((tick) => tick + 1);
    });
    return () => {
      live = false;
    };
    // The run's chart is frozen: once per run.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.id, runNeedsLayout]);
  const runGraph = run ? (runNeedsLayout ? (runLaid?.runId === run.id ? runLaid.graph : null) : run.graph) : null;

  const { saving, discardUnsaved } = doc;
  // Locked while edits here were unsaved: they can no longer be saved, so the chart goes back to the
  // version the lock holds, and Run judges that version rather than waiting on a save that cannot come.
  // A save in flight when the lock arrived is let land first: its ending runs this again. Never
  // silently: an editor whose changes go is told who locked it.
  useEffect(() => {
    if (lock === null) return;
    if (discardUnsaved() && mayEdit) toastManager.add({ type: "warning", title: lockDiscardedEdits(lock, nameOf) });
    // A new lock, or a save that settles while locked, moves this; the rest is read, not watched.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [lock?.lockedAt, saving]);

  // A lock closes a browser task being made for the chart: it could no longer be put on a step.
  useEffect(() => {
    if (lock !== null) setTaskDialog(null);
  }, [lock?.lockedAt]);

  // Closing the tab with edits not yet saved asks first, a failed or refused save included.
  const unsaved = saveState !== "saved";
  useEffect(() => {
    if (!unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  // Checked as it will be saved and run (a cleared name takes its step kind's name).
  const issues = useMemo(() => validateWorkflowGraph(graphForSave(graph)) as ReadonlyArray<WorkflowGraphIssue>, [graph]);
  const issueMap = useMemo(
    () => issuesByNode(runRefusals?.graph === graph ? [...issues, ...runRefusals.issues] : issues),
    [graph, issues, runRefusals],
  );
  const shownGraph = viewing ? runGraph : graph;
  const steps = useMemo(() => stepsByNode(run), [run]);
  const runEnded = run !== null && !isActiveRun(run);
  // Only the person who started a run may retry its steps (they act, and are billed, as them); they or the owner may stop it.
  const { account } = useAccountProfile();
  const mayRetry = canRetryRun(run, account?.email);
  // Who an Approval step can name: the organization's members, when they can be read.
  const { profile } = useOrgProfile(organizationId);
  const members = useMemo<ReadonlyArray<ApproverChoice> | null>(
    () => (profile ? profile.team.map((member) => ({ email: member.email, name: member.name })) : null),
    [profile],
  );
  const nameOf = useCallback(
    (email: string) => members?.find((member) => member.email.toLowerCase() === email.toLowerCase())?.name || null,
    [members],
  );

  const retry = useCallback(
    (nodeId: string, withCurrentSettings = false) => {
      if (!runId) return;
      setRetrying(nodeId);
      void retryWorkflowStep(organizationId, workflow.id, runId, nodeId, withCurrentSettings).then((outcome) => {
        setRetrying(null);
        if (!outcome.ok) toastManager.add({ type: "error", title: outcome.message });
      });
    },
    [organizationId, runId, workflow.id],
  );
  const describe = useCallback((node: WorkflowNode) => stepSubtitle(node, taskNames), [taskNames]);

  const makeStep = useCallback(
    (kind: WorkflowNodeType, at: Point) => {
      const fresh = newStep(currentGraph(), kind, at);
      // A workflow filed in a space saves into it unless told otherwise.
      return fresh.type === "save-file" && workflow.spaceId ? { ...fresh, config: { ...fresh.config, spaceId: workflow.spaceId } } : fresh;
    },
    [currentGraph, workflow.spaceId],
  );
  const onInsert = useCallback(
    (kind: WorkflowNodeType, edgeId: string, at: Point, push: number) => {
      const node = makeStep(kind, at);
      if (!insertStepOnLink(currentGraph(), edgeId, node, push)) return null;
      edit((current) => insertStepOnLink(current, edgeId, node, push) ?? current);
      return node.id;
    },
    [currentGraph, edit, makeStep],
  );
  const onAdd = useCallback(
    (kind: WorkflowNodeType, at: Point, link: { readonly source: string; readonly port: PortId } | null) => {
      const node = makeStep(kind, at);
      edit((current) => {
        const withNode = { ...current, nodes: [...current.nodes, node] };
        if (!link) return withNode;
        const handle = handleOfPort(link.port);
        return linkRefusal(withNode, link.source, node.id, handle) === null ? addLink(withNode, link.source, node.id, handle) : withNode;
      });
      return node.id;
    },
    [edit, makeStep],
  );
  const onConnect = useCallback(
    (source: string, port: PortId, target: string) => {
      const handle = handleOfPort(port);
      const refusal = linkRefusal(currentGraph(), source, target, handle);
      if (refusal === null) edit((current) => addLink(current, source, target, handle));
      else if (refusal !== "duplicate") toastManager.add({ type: "warning", title: LINK_REFUSAL[refusal] });
    },
    [currentGraph, edit],
  );
  const onMove = useCallback((positions: ReadonlyMap<string, Point>) => edit((current) => moveSteps(current, positions)), [edit]);
  const onRemove = useCallback(
    (removal: CanvasRemoval) => {
      holdChartFocus();
      if (removal.type === "nodes") {
        const ids = new Set(removal.ids);
        edit((current) => removeSteps(current, ids));
        setSelectedId((current) => (current && ids.has(current) ? null : current));
      } else {
        edit((current) => ({ ...current, edges: current.edges.filter((edge) => edge.id !== removal.id) }));
      }
    },
    [edit, holdChartFocus],
  );

  const tidy = () =>
    void tidyWorkflowGraph(currentGraph()).then((laid) => {
      // Only the new positions: anything edited while the layout ran is kept.
      const positions = new Map(laid.nodes.map((node) => [node.id, node.position]));
      edit((current) => ({ ...moveSteps(current, positions), layout: "down" }));
      setFitTick((tick) => tick + 1);
    });

  const startRun = async (input: string, files: ReadonlyArray<WorkflowRunFile> = []) => {
    const outcome = await startWorkflowRun(organizationId, workflow.id, input, files);
    // Refusals about a step (a Browser task step's task a draft, or gone) mark it on the chart.
    setRunRefusals(outcome.kind === "invalid" ? { graph, issues: outcome.issues } : null);
    if (outcome.kind === "started") {
      setSelectedId(null);
      onSelectRun(outcome.run.id);
      return null;
    }
    return outcome.kind === "invalid" ? { message: outcome.message, issues: outcome.issues } : { message: outcome.message };
  };

  const stop = () => {
    if (!runId) return;
    void cancelWorkflowRun(organizationId, workflow.id, runId).then((outcome) => {
      if (!outcome.ok) toastManager.add({ type: "error", title: outcome.message });
    });
  };

  const selectedNode = shownGraph?.nodes.find((node) => node.id === selectedId) ?? null;
  const startNode = graph.nodes.find((node) => node.type === "start");
  const runBlocked = issues.length > 0 || saveState !== "saved";
  const runBlockedReason =
    issues.length > 0
      ? `Fix ${issues.length === 1 ? "1 problem" : `${issues.length} problems`} first`
      : saveState === "saving" || saveState === "unsaved"
        ? "Saving…"
        : saveState === "error"
          ? "Save first"
          : null;

  const changeBar =
    !viewing && changes.sets.length > 0 ? (
      <ChangeBar
        sets={changes.sets}
        canUndo={canEdit}
        onChart={(step) => graph.nodes.some((node) => node.id === step)}
        onUndo={undoRequest}
        onShow={(steps) => setShowSteps((last) => ({ key: (last?.key ?? 0) + 1, steps }))}
        onDismiss={changes.dismiss}
        onFocusLeaving={holdChartFocus}
        onOpenChat={openAskEveOn}
      />
    ) : null;
  const eveFor = eveAtWork(changes.sets);
  const eveEditing = changes.sets.some(({ author }) => author.kind === "eve" && author.state === "editing");
  const askEveChart = panelChartState({ viewingRun: viewing, editable: canEdit, graph });
  // A run shows its bar under the header (AMO-306); the chart keeps a notice only while there is no run to show.
  const chartNotice = viewing && run ? null : viewing ? (
    <div className={cn(GLASS_PILL, "pointer-events-auto animate-canvas-rise-in font-normal whitespace-nowrap text-muted-foreground")} data-workflow-run-notice="">
      {runView.read?.kind === "missing" ? (
        <span>This run no longer exists.</span>
      ) : runView.read?.kind === "unavailable" ? (
        <span className="text-destructive">The run could not be loaded.</span>
      ) : (
        <span>Loading the run…</span>
      )}
      <Button size="xs" variant="secondary" onClick={() => onSelectRun(null)}>
        Back to editing
      </Button>
    </div>
  ) : lock ? (
    <div className={cn(GLASS_PILL, "pointer-events-auto pr-3.5 font-normal text-muted-foreground")} data-workflow-locked="">
      <LockIcon className="size-3.5" aria-hidden />
      {lockSummary(lock, nameOf)}
    </div>
  ) : !canEdit ? (
    <div className={cn(GLASS_PILL, "pointer-events-auto pr-3.5 font-normal text-muted-foreground")} data-workflow-read-only="">
      Shared with you to run. Only its editors can change it.
    </div>
  ) : selectedNode && issues.length > 0 ? (
    // The step's panel covers the pill's corner: a merge can still leave a problem to tell of.
    <IssuesPill issues={issues} graph={graph} onPick={setSelectedId} />
  ) : null;
  const notice = chartNotice && changeBar ? (
    <>
      {chartNotice}
      {changeBar}
    </>
  ) : (chartNotice ?? changeBar);

  return (
    <div
      className={SURFACE}
      onKeyDown={(event) => {
        if (askEve.chartHears(event)) chartKeys.current?.(event.nativeEvent);
      }}
      data-workflow-page={workflow.id}
    >
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 sm:px-4">
        <SidebarTrigger className="md:hidden" aria-label="Open Home sidebar" />
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label="All workflows"
          render={<Link to="/$org/workflows" params={{ org }} />}
        >
          <ArrowLeftIcon />
        </Button>
        <input
          value={name}
          onChange={(event) => doc.rename(event.currentTarget.value)}
          disabled={locked}
          maxLength={200}
          aria-label="Workflow name"
          className="min-w-0 flex-1 rounded-md bg-transparent px-1.5 py-1 text-[15px] font-semibold text-foreground outline-none hover:bg-muted/50 focus:bg-muted/60 disabled:hover:bg-transparent"
          data-workflow-name=""
        />
        {!viewing ? (
          <span
            className={saveState === "error" ? "text-xs text-destructive" : "text-xs text-muted-foreground"}
            data-workflow-save-state={saveState}
            title={saveError ?? undefined}
          >
            {SAVE_LABEL[saveState]}
          </span>
        ) : null}
        {!viewing && saveState === "error" ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={doc.retry}
            data-workflow-save-retry=""
          >
            Retry
          </Button>
        ) : null}
        <div className="flex items-center rounded-md bg-muted/60 p-0.5" role="tablist" aria-label="Edit or runs" data-workflow-mode={viewing ? "runs" : "edit"}>
          <button
            type="button"
            role="tab"
            aria-selected={!viewing}
            onClick={() => onSelectRun(null)}
            className={cn("rounded px-2 py-0.5 text-xs font-medium", !viewing ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground")}
            data-workflow-mode-edit=""
          >
            Edit
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewing}
            disabled={!viewing && runs.length === 0}
            title={!viewing && runs.length === 0 ? "No runs yet" : undefined}
            onClick={() => (viewing ? undefined : runs[0] ? onSelectRun(runs[0].id) : undefined)}
            className={cn(
              "rounded px-2 py-0.5 text-xs font-medium disabled:opacity-50",
              viewing ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground",
            )}
            data-workflow-mode-runs=""
          >
            Runs
          </button>
        </div>
        <Menu>
          <MenuTrigger render={<Button size="sm" variant="ghost" data-workflow-runs="" />}>
            <HistoryIcon />
            Runs{runs.length > 0 ? ` (${runs.length})` : ""}
            <ChevronDownIcon className="size-3.5 opacity-60" />
          </MenuTrigger>
          <MenuPopup align="end" className="w-72">
            {viewing ? (
              <>
                <MenuItem onClick={() => onSelectRun(null)}>
                  <ArrowLeftIcon className="size-3.5" />
                  Back to editing
                </MenuItem>
                <MenuSeparator />
              </>
            ) : null}
            {runs.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">No runs yet. Press Run to start one.</p>
            ) : (
              <MenuGroup>
                <MenuGroupLabel>Recent runs</MenuGroupLabel>
                {runs.map((entry) => (
                  <MenuItem key={entry.id} onClick={() => onSelectRun(entry.id)} data-workflow-run-entry={entry.id}>
                    <RunStatusPill status={entry.status} />
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {formatRelativeTimeLabel(entry.createdAt)}
                      {entry.startedByEmail ? ` · ${nameOf(entry.startedByEmail) ?? entry.startedByEmail}` : ""}
                    </span>
                  </MenuItem>
                ))}
              </MenuGroup>
            )}
          </MenuPopup>
        </Menu>
        <PeopleHere people={people} eveFor={eveFor} />
        <Button
          ref={askEveButtonRef}
          size="sm"
          variant={askEve.open ? "secondary" : "ghost"}
          aria-pressed={askEve.open}
          data-pressed={askEve.open || undefined}
          onClick={askEve.toggle}
          onKeyDown={claimKeyForPanel}
          data-workflow-ask-eve={askEve.open ? "open" : "closed"}
        >
          <span aria-hidden className="inline-flex [&_svg]:h-4">
            <AmoregMark />
          </span>
          Ask eve
          {askEve.shortcutLabel ? (
            <Kbd aria-hidden className="hidden md:inline-flex">
              {askEve.shortcutLabel}
            </Kbd>
          ) : null}
        </Button>
        {role === "owner" && sharing ? (
          <Button size="sm" variant="ghost" onClick={() => setShareDialog(true)} data-workflow-share="">
            <Share2Icon />
            {sharing.everyone !== null || sharing.people.length > 0 ? "Shared" : "Share"}
          </Button>
        ) : null}
        {mayEdit ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLockDialog(true)}
            aria-label={lock ? "Locked: unlock" : "Lock"}
            title={lock ? lockSummary(lock, nameOf) : "Lock it so nobody can change it"}
            data-workflow-lock={lock ? "locked" : "unlocked"}
          >
            {lock ? <LockIcon /> : <LockOpenIcon />}
            {lock ? "Locked" : "Lock"}
          </Button>
        ) : null}
        {mayEdit ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setScheduleDialog(true)}
          title={workflow.schedule?.lastError ?? undefined}
          className={workflow.schedule?.lastError ? "text-destructive" : undefined}
          data-workflow-schedule={workflow.schedule ? "on" : "off"}
        >
          <CalendarClockIcon />
          <span className="max-w-48 truncate">{workflow.schedule ? workflow.schedule.description : "Schedule"}</span>
        </Button>
        ) : null}
        <Button
          size="sm"
          onClick={() => setRunDialog(true)}
          disabled={runBlocked}
          title={runBlockedReason ?? undefined}
          data-workflow-run=""
        >
          <PlayIcon />
          Run
        </Button>
        {canEdit ? (
        <Menu>
          <MenuTrigger render={<Button size="icon-sm" variant="ghost" aria-label="More" />}>
            <EllipsisIcon />
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem variant="destructive" onClick={() => setDeleting(true)}>
              <Trash2Icon className="size-4" />
              Delete workflow
            </MenuItem>
          </MenuPopup>
        </Menu>
        ) : null}
      </header>

      {viewing && run ? (
        <RunBar
          run={run}
          nameOf={nameOf}
          viewerEmail={account?.email}
          mayRetry={mayRetry}
          retrying={retrying !== null}
          onStop={role === "owner" || mayRetry ? stop : null}
          onRetry={(nodeId) => retry(nodeId)}
          onRunAgain={() => setRunDialog(true)}
          // The same gate as the header's Run: never the saved chart while the one shown differs from it.
          runAgainBlocked={runBlocked ? (runBlockedReason ?? "Save first") : null}
          onOpenStep={(nodeId) => setSelectedId(nodeId)}
        />
      ) : null}

      <div className="relative flex min-h-0 flex-1">
        <div
          ref={chartRef}
          tabIndex={-1}
          inert={askEve.open && askEve.mobile}
          onPointerDown={focusContainerOnPress}
          className="relative flex min-h-0 min-w-0 flex-1 outline-none"
          data-workflow-chart-column=""
        >
          {shownGraph ? (
            <WorkflowCanvas
              label={workflow.name}
              graph={shownGraph}
              editable={!locked}
              locked={!canEdit && !viewing}
              runSteps={viewing ? steps : null}
              issues={issueMap}
              describe={describe}
              startPill={workflow.schedule ? "Scheduled" : "On run"}
              now={now}
              openId={selectedNode ? selectedNode.id : null}
              onOpen={openStep}
              onAdd={onAdd}
              onInsert={onInsert}
              onConnect={onConnect}
              onMove={onMove}
              onRemove={onRemove}
              onUndo={undo}
              onRedo={redo}
              onTidy={canEdit ? tidy : null}
              onRetry={mayRetry ? retry : undefined}
              retrying={retrying}
              fitKey={`${viewing ? `run:${runId}` : "editor"}:${fitTick}:${askEve.besideWidth}`}
              keysRef={chartKeys}
              settling={!viewing && !relayoutFailed && needsRelayout(graph)}
              notice={notice}
              peers={presence.peers}
              onPresence={presence.publish}
              revisions={doc.subscribe}
              show={viewing ? null : showSteps}
              marks={viewing ? undefined : changes.marks}
              status={viewing ? null : <IssuesPill issues={issues} graph={graph} onPick={setSelectedId} />}
              renderHoverCard={
                viewing
                  ? (node) => (
                      <StepQuickRun
                        type={node.type}
                        step={steps.get(node.id) ?? null}
                        now={now}
                        onRetry={mayRetry && (runEnded || steps.get(node.id)?.status === "failed") ? () => retry(node.id) : null}
                        retrying={retrying === node.id}
                        org={org}
                      />
                    )
                  : locked
                    ? undefined
                    : (node, { close }) => (
                        <StepFieldScope stepId={node.id} publish={presence.publish}>
                          <StepQuickEdit
                            node={node}
                            onChange={(next) => edit((current) => updateStep(current, next.id, () => next), `step:${next.id}`)}
                            onDuplicate={
                              node.type === "start"
                                ? null
                                : () => {
                                    // Beside the original, in the first free place to its right.
                                    const at = freePlacement({ x: node.position.x + NODE_WIDTH + 40, y: node.position.y }, currentGraph().nodes, {});
                                    edit((current) => duplicateStep(current, node.id, at)?.graph ?? current);
                                    close();
                                  }
                            }
                            onDelete={
                              node.type === "start"
                                ? null
                                : () => {
                                    holdChartFocus();
                                    edit((current) => removeSteps(current, new Set([node.id])));
                                    setSelectedId((current) => (current === node.id ? null : current));
                                    close();
                                  }
                            }
                          />
                        </StepFieldScope>
                      )
              }
              renderInspector={(helpers) =>
                selectedNode && !locked ? (
                  <StepFieldScope stepId={selectedNode.id} publish={presence.publish}>
                    <StepEditorPanel
                      node={selectedNode}
                      onChange={(next) => edit((current) => updateStep(current, next.id, () => next), `step:${next.id}`)}
                      onDelete={() => {
                        holdChartFocus();
                        edit((current) => removeSteps(current, new Set([selectedNode.id])));
                        setSelectedId(null);
                      }}
                      onClose={() => openStep(null)}
                      canDelete={selectedNode.type !== "start"}
                      browserTasks={browserTasks}
                      onOpenBrowserTask={(taskId) => setTaskDialog({ taskId })}
                      members={members}
                      organizationId={organizationId}
                      workflowId={workflow.id}
                      spaceId={workflow.spaceId}
                      graph={graph}
                      footer={
                        <NextStepFooter
                          node={selectedNode}
                          graph={graph}
                          onWalk={(id) => {
                            setSelectedId(id);
                            helpers.focusNode(id);
                          }}
                          onAdd={(kind) => helpers.appendTo(selectedNode.id, kind)}
                        />
                      }
                    />
                  </StepFieldScope>
                ) : selectedNode && viewing ? (
                  <StepRunPanel
                    node={selectedNode}
                    step={steps.get(selectedNode.id) ?? null}
                    now={now}
                    onRetry={
                      mayRetry && (runEnded || steps.get(selectedNode.id)?.status === "failed") ? () => retry(selectedNode.id) : null
                    }
                    // Only once the chart is saved: the rerun takes the workflow's settings as the server holds them.
                    onRerunWithCurrent={mayRetry && runEnded && saveState === "saved" ? () => retry(selectedNode.id, true) : null}
                    retrying={retrying === selectedNode.id}
                    onClose={() => openStep(null)}
                    org={org}
                  >
                    {selectedNode.type === "approval" && run ? (
                      <ApprovalDecision organizationId={organizationId} run={run} nodeId={selectedNode.id} viewerEmail={account?.email} />
                    ) : null}
                    {selectedNode.type === "checklist" && run ? (
                      <ChecklistDone organizationId={organizationId} run={run} nodeId={selectedNode.id} viewerEmail={account?.email} />
                    ) : null}
                    {selectedNode.type === "intake" && run ? (
                      <>
                        <MailboxWait organizationId={organizationId} run={run} nodeId={selectedNode.id} viewerEmail={account?.email} />
                        <MailboxReads run={run} nodeId={selectedNode.id} />
                      </>
                    ) : null}
                  </StepRunPanel>
                ) : null
              }
            />
          ) : (
            // No chart to show yet (a run loading), or none at all (a run gone): its bar still says so, with Back to editing.
            <div className="workflow-canvas relative min-h-0 flex-1 bg-canvas-panel" data-workflow-canvas="">
              {notice ? <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2">{notice}</div> : null}
              {viewing && (runView.read?.kind === "missing" || runView.read?.kind === "unavailable") ? null : (
                <div className="p-6 pt-20">
                  <ListSkeleton rows={3} />
                </div>
              )}
            </div>
          )}
          {askEveTabShown({ panelOpen: askEve.open, stepOpen: selectedNode !== null, roomBeside: !askEve.mobile }) ? (
            <AskEveTab onOpen={askEve.toggle} live={eveEditing} />
          ) : null}
        </div>
        <AskEvePanel
          organizationId={organizationId}
          workflowId={workflow.id}
          chart={askEveChart}
          request={askEve.request}
          open={askEve.open}
          width={askEve.width}
          mobile={askEve.mobile}
          shortcutLabel={askEve.shortcutLabel}
          panelRef={askEvePanelRef}
          buttonRef={askEveButtonRef}
          onClose={askEve.close}
          onResize={askEve.resize}
        />
      </div>

      <BrowserTaskDialog
        organizationId={organizationId}
        taskId={taskDialog?.taskId ?? null}
        open={taskDialog !== null}
        onOpenChange={(open) => (open ? undefined : setTaskDialog(null))}
        onCreated={(task) => {
          // A task made from a step's panel is that step's task, when it has none yet; never on a locked chart.
          if (!selectedId || !canEdit) return;
          edit((current) =>
            updateStep(current, selectedId, (node) =>
              node.type === "browser-task" && !node.config.taskId ? { ...node, config: { ...node.config, taskId: task.id } } : node,
            ),
          );
        }}
      />
      <RunDialog
        open={runDialog}
        onOpenChange={setRunDialog}
        hint={startNode?.type === "start" ? startNode.config.inputHint : undefined}
        filesHint={startNode?.type === "start" && startNode.config.files?.ask ? startNode.config.files.hint : undefined}
        onStart={startRun}
      />
      {sharing ? (
        <WorkflowShareDialog
          open={shareDialog}
          onOpenChange={setShareDialog}
          organizationId={organizationId}
          workflowId={workflow.id}
          sharing={sharing}
          members={members}
          ownerEmail={workflow.createdByEmail}
        />
      ) : null}
      {mayEdit ? (
        <WorkflowLockDialog
          open={lockDialog}
          onOpenChange={setLockDialog}
          organizationId={organizationId}
          workflowId={workflow.id}
          lock={lock}
          history={lockHistory}
          nameOf={nameOf}
          waitForSave={lockWaitsForSave(saveState)}
        />
      ) : null}
      <ScheduleDialog
        open={scheduleDialog}
        onOpenChange={setScheduleDialog}
        schedule={workflow.schedule ?? null}
        organizationId={organizationId}
        workflowId={workflow.id}
        runBlockedReason={issues.length > 0 ? runBlockedReason : null}
      />
      <ConfirmDeleteDialog
        open={deleting}
        onOpenChange={setDeleting}
        name={workflow.name}
        onConfirm={async () => {
          const outcome = await deleteWorkflow(organizationId, workflow.id);
          if (!outcome.ok) {
            toastManager.add({ type: "error", title: outcome.message });
            return;
          }
          setDeleting(false);
          void navigate({ to: "/$org/workflows", params: { org } });
        }}
      />
    </div>
  );
}

export function WorkflowPage({
  workflowId,
  runId,
  stepId = null,
  chatId = null,
  onSelectRun,
}: {
  readonly workflowId: string;
  readonly runId: string | null;
  readonly stepId?: string | null;
  readonly chatId?: string | null;
  readonly onSelectRun: (runId: string | null) => void;
}) {
  const activeOrg = useActiveOrg();
  const organizationId = activeOrg?.id ?? null;
  const { read } = useWorkflow(organizationId, workflowId);
  if (!organizationId || read === null) {
    return (
      <div className={SURFACE}>
        <div className="p-6">
          <ListSkeleton rows={4} />
        </div>
      </div>
    );
  }
  if (read.kind !== "ready") {
    return (
      <div className={SURFACE}>
      <div className="mx-auto max-w-md px-6 py-20 text-center" data-workflow-missing="">
        <p className="text-sm font-medium text-foreground">
          {read.kind === "missing" ? "This workflow was deleted or is not in this organization." : "The workflow could not be loaded."}
        </p>
        <Button className="mt-4" size="sm" variant="outline" render={<Link to="/$org/workflows" params={{ org: orgUrlSegment(organizationId) }} />}>
          All workflows
        </Button>
      </div>
      </div>
    );
  }
  return (
    <WorkflowChart
        key={read.value.workflow.id}
        organizationId={organizationId}
        workflow={read.value.workflow}
        sharing={read.value.sharing ?? null}
        lockHistory={read.value.lockHistory ?? []}
        runs={read.value.runs}
        runId={runId}
        stepId={stepId}
        chatId={chatId}
        onSelectRun={onSelectRun}
      />
  );
}
